import React from 'react';
import { Box, Grid, Paper, Tooltip, Typography, ButtonGroup, Button } from '@mui/material';
import styles from './IncidentTab.module.css';
import useIncidents from 'containers/pages/CANguard/hooks/useIncidents';
import PatrolSelection from './PatrolSelection';
import CustomerAddressSelection from './CustomerAddressSelection';
import IncidentSelector from './IncidentSelection';
import StartTimeSelection from './StartTimeSelection';
import LoadingBackdrop from 'components/LoadingBackdrop';
import { GuardTabs } from 'pages/CANguard/CANguard';
import { useGuardRoleContext } from 'containers/pages/CANguard/context/GuardRoleProvider';

interface IncidentTabProps {
	goToTab: (tab: GuardTabs) => void;
}

const IncidentTab: React.FC<IncidentTabProps> = (props) => {
	const { states, actions } = useIncidents(props.goToTab);
	const { isAdmin, guardSelectorOptions } = useGuardRoleContext();

	const isFormIncomplete = (): boolean => {
		if (!states.selectedIncidentChecklist) return true;
		if (states.workTimeLoss) {
			if (!states.selectedCustomer || !states.selectedAddress || !states.startTime) return true;
		} else {
			if (!states.selectedPatrol) return true;
		}
		return false;
	};

	const getCreateButtonTooltip = (): string => {
		const missing: string[] = [];
		if (!states.selectedIncidentChecklist) missing.push('incident type');
		if (states.workTimeLoss) {
			if (!states.selectedCustomer) missing.push('customer');
			if (!states.selectedAddress) missing.push('address');
			if (!states.startTime) missing.push('start time');
		} else {
			if (!states.selectedPatrol) missing.push('patrol');
		}
		if (missing.length === 0) return '';
		return `Please select: ${missing.join(', ')}`;
	};

	return (
		<Box className={styles.container}>
			<Paper elevation={1} sx={{ width: '100%', maxWidth: '500px', overflow: 'visible' }}>
			<Grid className={styles.maxWidth}>
				<Grid item>
					<Typography variant="h6" fontStyle={'italic'}>
						Create Incident{' '}
						{isAdmin && (
							<span className={styles.titleInfo}>
								as {guardSelectorOptions.selectedGuardName}
							</span>
						)}
					</Typography>
				</Grid>
				{/* SECTION: LOSS OF WORK TIME SELECTION: */}
				<Grid item className={styles.sectionContainer}>
					<Typography component="div">
						Did this incident result in loss of work time?
					</Typography>
					<Box>
						<ButtonGroup
							variant="contained"
							aria-label="Loss of work time"
						>
							<Button
								variant={
									states.workTimeLoss
										? 'contained'
										: 'outlined'
								}
								color="primary"
								onClick={() =>
									actions.handleWorkTimeLossChange(true)
								}
							>
								Yes
							</Button>
							<Button
								variant={
									!states.workTimeLoss
										? 'contained'
										: 'outlined'
								}
								color="primary"
								onClick={() =>
									actions.handleWorkTimeLossChange(false)
								}
							>
								No
							</Button>
						</ButtonGroup>
					</Box>
				</Grid>

				{/* SECTION: PATROL SELECTION: */}
				{!states.workTimeLoss && (
					<PatrolSelection
						selectedPatrol={states.selectedPatrol}
						selectedPatrolInputError={states.inputValidation.patrol}
						handleSelectedPatrolChange={
							actions.handleSelectedPatrolChange
						}
					/>
				)}

				{/* SECTION: CUSTOMER & ADDRESS SELECTION: */}
				{states.workTimeLoss && (
					<CustomerAddressSelection
						addressInputError={states.inputValidation.address}
						customerInputError={states.inputValidation.customer}
						handleSelectedAddressChange={
							actions.handleSelectedAddressChange
						}
						handleSelectedCustomerChange={
							actions.handleSelectedCustomerChange
						}
						selectedAddress={states.selectedAddress}
						selectedCustomer={states.selectedCustomer}
					/>
				)}

				{/* SECTION: INCIDENT TYPE SELECTION: */}
				<IncidentSelector
					selectedIncidentChecklist={states.selectedIncidentChecklist}
					incidentChecklistInputError={
						states.inputValidation.incidentType
					}
					handleSelectedIncidentChecklistChange={
						actions.handleSelectedIncidentChecklistChange
					}
				/>

				{/* SECTION: TIME SELECTION: */}
				{states.workTimeLoss && (
					<StartTimeSelection
						startTime={states.startTime}
						handleStartTimeChange={actions.handleStartTimeChange}
						startTimeInputError={states.inputValidation.startTime}
					/>
				)}

				{/* SECTION: CREATE INCIDENT: */}
				<Grid item className={styles.sectionContainer}>
					<Tooltip
						title={getCreateButtonTooltip()}
						disableHoverListener={!isFormIncomplete()}
					>
						<span>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								onClick={actions.handleSubmit}
								disabled={isFormIncomplete()}
							>
								Create
							</Button>
						</span>
					</Tooltip>
				</Grid>
				<LoadingBackdrop open={states.loading} />
			</Grid>
			</Paper>
		</Box>
	);
};

export default IncidentTab;
